// Shared query + mutation surface for the /me/onboarding-progress endpoint.
// Two consumers today: the visible `<OnboardingChecklist>` card on /feed,
// and the always-mounted `useOnboardingVisitTracker()` hook in the shell
// that auto-completes `signal: 'visit'` items when the user actually lands
// on the linked route (rather than only when they click Skip on the card).
//
// Centralizing the cache key + mutation lets both consumers share one
// optimistic-update path. Without that, the tracker would invalidate the
// card's cache (or vice versa) and one would flicker as it refetched.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { getApiClient } from '@/lib/api-client';
import { throwApiError } from '@/lib/api-error';
import type { components } from '@leanin/api-client';

export type OnboardingProgress = components['schemas']['OnboardingProgress'];
export type OnboardingAction = components['schemas']['OnboardingAction'];
export type OnboardingActionKey =
  components['schemas']['RecordOnboardingActionBody']['actionKey'];

export const onboardingKeys = {
  progress: () => ['onboarding', 'progress'] as const,
};

// Marks a *derived* onboarding step complete the moment the user performs the
// underlying action (creating a post → `first_post`, etc.), instead of waiting
// for the async event-subscriber worker to record it + a manual refetch.
//
// Call from the mutation that performs the action. It (1) optimistically
// patches the progress cache so the checklist advances instantly, and (2)
// persists via /me/onboarding-actions so it's durable even if the worker lags
// or is down — the worker's later record is idempotent (ON CONFLICT). No-ops
// when the viewer isn't onboarding, has dismissed it, doesn't have this step,
// or already has an action recorded for it (so an explicit skip is respected).
export async function completeOnboardingActionIfPending(
  qc: QueryClient,
  actionKey: OnboardingActionKey,
): Promise<void> {
  const progress = qc.getQueryData<OnboardingProgress>(onboardingKeys.progress());
  if (progress === undefined || progress.dismissedAt !== null) return;
  if (!progress.steps.includes(actionKey)) return;
  if (progress.actions.some((a) => a.key === actionKey)) return;

  qc.setQueryData<OnboardingProgress>(onboardingKeys.progress(), {
    ...progress,
    actions: [
      ...progress.actions,
      { key: actionKey, kind: 'completed', completedAt: new Date().toISOString() },
    ],
  });

  try {
    const client = getApiClient();
    await client.POST('/api/v1/me/onboarding-actions', {
      body: { actionKey, kind: 'completed' },
    });
  } catch {
    // Best-effort: the worker is the authoritative backstop. A transient
    // failure here just means the optimistic state reconciles on the next
    // progress refetch.
  }
}

export async function fetchOnboardingProgress(): Promise<OnboardingProgress> {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/me/onboarding-progress');
  if (data === undefined) throwApiError(error);
  return data;
}

export function useOnboardingProgress() {
  return useQuery({
    queryKey: onboardingKeys.progress(),
    queryFn: fetchOnboardingProgress,
  });
}

export function useRecordOnboardingAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      actionKey: OnboardingActionKey;
      kind: 'completed' | 'skipped';
    }) => {
      const client = getApiClient();
      const { response } = await client.POST('/api/v1/me/onboarding-actions', {
        body: { actionKey: vars.actionKey, kind: vars.kind },
      });
      if (!response.ok) throw new Error(`onboarding-actions failed (${response.status})`);
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: onboardingKeys.progress() });
      const previous = qc.getQueryData<OnboardingProgress>(onboardingKeys.progress());
      if (previous !== undefined) {
        const existing = previous.actions.find((a) => a.key === vars.actionKey);
        const newAction: OnboardingAction = {
          key: vars.actionKey,
          kind: vars.kind,
          completedAt: new Date().toISOString(),
        };
        qc.setQueryData<OnboardingProgress>(onboardingKeys.progress(), {
          ...previous,
          actions: existing !== undefined
            ? previous.actions.map((a) => a.key === vars.actionKey ? newAction : a)
            : [...previous.actions, newAction],
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(onboardingKeys.progress(), ctx.previous);
      }
    },
  });
}

export function useDismissOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const client = getApiClient();
      const { response } = await client.POST('/api/v1/me/onboarding-dismissed');
      if (!response.ok) throw new Error(`onboarding-dismissed failed (${response.status})`);
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: onboardingKeys.progress() });
      const previous = qc.getQueryData<OnboardingProgress>(onboardingKeys.progress());
      if (previous !== undefined) {
        qc.setQueryData<OnboardingProgress>(onboardingKeys.progress(), {
          ...previous,
          dismissedAt: new Date().toISOString(),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(onboardingKeys.progress(), ctx.previous);
      }
    },
  });
}
